import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Landmark, TreePine, ArrowRight, Users, Briefcase, Palmtree } from "lucide-react";
import { MARKETS_COPY } from "../../content/site";
import { Wordmark, GoldDivider, Eyebrow, EditorialHeading } from "../../components/brand";
import SectionHeader from "../../components/shared/SectionHeader";
import OpportunityCard from "../../components/shared/OpportunityCard";
import ArticleCard from "../../components/shared/ArticleCard";
import LeadForm from "../../components/shared/LeadForm";
import { api } from "../../lib/api";
import { ARTICLES } from "../../content/site";

const propertyIcons = { Residential: Building2, Luxury: Palmtree, Land: TreePine, Commercial: Briefcase, "New Developments": Landmark, "Vacation Property": Palmtree, "Business Opportunity": Users };

export default function ExploreGhana() {
  const m = MARKETS_COPY.ghana;
  const [opps, setOpps] = useState([]);

  useEffect(() => {
    api.get("/public/opportunities?market=Ghana").then((r) => setOpps(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const ghanaArticles = ARTICLES.filter((a) => a.market === "Ghana");

  return (
    <div data-testid="page-explore-ghana">
      {/* HERO */}
      <section className="relative -mt-24 h-[80vh] min-h-[560px] overflow-hidden bg-altura-espresso">
        <img src={m.hero} alt="Ghana" className="absolute inset-0 h-full w-full object-cover animate-ken-burns" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="relative z-10 flex h-full items-end pt-24">
          <div className="container-editorial pb-20 text-altura-ivory">
            <Eyebrow className="text-altura-gold">🇬🇭 Flagship Market</Eyebrow>
            <EditorialHeading size="xl" className="mt-4 text-altura-ivory max-w-3xl">
              Ghana. <span className="italic text-altura-gold-light">Steady. Confident.</span> Coming home.
            </EditorialHeading>
            <p className="mt-5 max-w-xl text-altura-ivory/85 text-lg leading-relaxed">{m.intro}</p>
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="bg-altura-ivory py-24" data-testid="ghana-overview">
        <div className="container-editorial grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Eyebrow>Overview</Eyebrow>
            <EditorialHeading size="md" className="mt-4">The heart of the Altura Global thesis.</EditorialHeading>
            <GoldDivider className="mt-6" />
            <p className="mt-6 text-altura-charcoal/75 leading-relaxed">
              Ghana rewards patience. It is not a short-term flip market — it is a market for those who understand the arc: legal diligence, developer relationships, and long-hold appreciation aligned with a maturing economy. Altura Global exists to help you navigate it, without shortcuts.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {[
                { k: "English", l: "Common-law legal system" },
                { k: "USD/GHS", l: "USD often accepted on premium assets" },
                { k: "Diaspora", l: "Structured buying pathways" },
                { k: "Titled", l: "Rigorous title verification" },
              ].map((x) => (
                <div key={x.k}>
                  <div className="font-editorial text-2xl text-altura-espresso">{x.k}</div>
                  <div className="mt-1 text-xs text-altura-charcoal/60">{x.l}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-3">
              <img src="https://images.pexels.com/photos/32803403/pexels-photo-32803403.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900" alt="Accra" className="aspect-[3/4] object-cover rounded-lg" />
              <div className="space-y-3">
                <img src="https://images.pexels.com/photos/18285958/pexels-photo-18285958.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=520&w=520" alt="Ghana interior" className="aspect-square object-cover rounded-lg" />
                <img src="https://images.pexels.com/photos/20418771/pexels-photo-20418771.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=520&w=520" alt="Ghana architectural" className="aspect-square object-cover rounded-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHY GHANA */}
      <section className="bg-altura-beige/40 py-24" data-testid="ghana-why">
        <div className="container-editorial">
          <SectionHeader eyebrow="Why Ghana" title="Four reasons investors keep returning." align="center" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {m.why_points.map((p, i) => (
              <div key={p.title} className="card-editorial p-8" data-testid={`why-ghana-${i}`}>
                <div className="eyebrow text-altura-gold-dark">0{i + 1}</div>
                <h3 className="mt-3 font-editorial text-2xl text-altura-espresso">{p.title}</h3>
                <p className="mt-3 text-sm text-altura-charcoal/75 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROPERTY TYPES */}
      <section className="bg-altura-ivory py-24" data-testid="ghana-property-types">
        <div className="container-editorial">
          <SectionHeader eyebrow="Property Types" title="From titled land to signature villas." />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {m.property_types.map((t, i) => {
              const Icon = propertyIcons[t] || Building2;
              return (
                <Link key={t} to={`/opportunities?market=Ghana`} className="card-editorial p-6 group" data-testid={`property-type-${i}`}>
                  <Icon className="h-6 w-6 text-altura-gold-dark" />
                  <div className="mt-4 font-editorial text-lg text-altura-espresso">{t}</div>
                  <div className="mt-2 text-xs text-altura-espresso group-hover:text-altura-gold transition inline-flex items-center gap-1.5">Explore <ArrowRight className="h-3 w-3" /></div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* KEY AREAS */}
      <section className="bg-altura-espresso text-altura-ivory relative noise py-24" data-testid="ghana-key-areas">
        <div className="container-editorial relative z-10">
          <SectionHeader eyebrow="Key Areas" title="Where the market lives." align="left" className="text-altura-ivory" />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {m.key_areas.map((area, i) => (
              <div key={area} className="border-b border-altura-ivory/10 pb-6" data-testid={`ghana-area-${i}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <div className="font-editorial text-xl text-altura-ivory">{area.split("—")[0].trim()}</div>
                  <span className="font-mono text-[11px] text-altura-gold">/ 0{i + 1}</span>
                </div>
                <div className="mt-2 text-sm text-altura-ivory/60">{area.includes("—") ? area.split("—")[1].trim() : ""}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED GHANA OPPS */}
      {opps.length > 0 && (
        <section className="bg-altura-ivory py-24" data-testid="ghana-opportunities">
          <div className="container-editorial">
            <div className="flex items-end justify-between flex-wrap gap-4">
              <SectionHeader eyebrow="Featured Opportunities" title="A curated selection from our Ghana portfolio." size="md" />
              <Link to="/opportunities?market=Ghana" className="link-underline text-sm text-altura-espresso">View all →</Link>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {opps.map((o) => <OpportunityCard key={o.id} opportunity={o} />)}
            </div>
          </div>
        </section>
      )}

      {/* BUYING PROCESS */}
      <section className="bg-altura-beige/40 py-24" data-testid="ghana-process">
        <div className="container-editorial">
          <SectionHeader eyebrow="The Process" title="A clear, considered path from first conversation to closing." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-6 text-sm">
            {[
              { t: "Consultation", d: "Understand your objectives, timeline, and budget shape." },
              { t: "Curated Shortlist", d: "A restrained set of matched opportunities." },
              { t: "Tours", d: "Physical or virtual property visits with an Altura advisor." },
              { t: "Diligence", d: "Title verification through licensed local counsel." },
              { t: "Close", d: "Coordinated escrow, closing, and post-purchase onboarding." },
            ].map((s, i) => (
              <div key={s.t} className="relative pt-6 border-t border-altura-gold/30" data-testid={`process-step-${i}`}>
                <div className="font-mono text-xs text-altura-gold-dark">0{i + 1}</div>
                <div className="mt-2 font-editorial text-xl text-altura-espresso">{s.t}</div>
                <div className="mt-2 text-altura-charcoal/70">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GHANA ARTICLES */}
      {ghanaArticles.length > 0 && (
        <section className="bg-altura-ivory py-24" data-testid="ghana-articles">
          <div className="container-editorial">
            <SectionHeader eyebrow="Ghana Notes" title="Editorial writing on Ghana investing." size="md" />
            <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
              {ghanaArticles.slice(0, 3).map((a) => <ArticleCard key={a.slug} article={a} />)}
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="bg-altura-beige/40 py-24" data-testid="ghana-faqs">
        <div className="container-editorial max-w-4xl">
          <SectionHeader eyebrow="FAQs" title="Common questions about Ghana." />
          <div className="mt-12 space-y-6">
            {m.faqs.map((f, i) => (
              <details key={f.q} className="group border-b border-altura-espresso/15 pb-6" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="font-editorial text-xl text-altura-espresso">{f.q}</span>
                  <span className="text-altura-gold text-2xl leading-none transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-4 text-sm text-altura-charcoal/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / FORM */}
      <section className="bg-altura-espresso-2 text-altura-ivory py-24" data-testid="ghana-inquiry">
        <div className="container-editorial grid lg:grid-cols-2 gap-14">
          <div>
            <Eyebrow className="text-altura-gold">Request Information</Eyebrow>
            <EditorialHeading size="lg" className="mt-4 text-altura-ivory">Begin your Ghana journey.</EditorialHeading>
            <p className="mt-5 text-altura-ivory/70 max-w-md">
              Tell us about your objectives. An Altura Global Ghana specialist will follow up personally.
            </p>
            <GoldDivider className="mt-6" />
            <ul className="mt-6 space-y-2 text-sm text-altura-ivory/70">
              <li>· Curated shortlists within 5 business days</li>
              <li>· Diaspora-focused onboarding</li>
              <li>· Virtual tours available</li>
            </ul>
          </div>
          <div className="rounded-lg border border-altura-ivory/10 bg-altura-ivory/5 p-8">
            <LeadForm defaultMarket="Ghana" sourcePage="/explore-ghana" testIdPrefix="ghana-inquiry" />
          </div>
        </div>
      </section>
    </div>
  );
}
